import { computed, ref } from "vue";
import {
  validatePassword,
  validateUsername,
  getPasswordGuidelines,
  getUsernameGuidelines,
  PASSWORD_CONFIG,
  USERNAME_CONFIG,
  type ValidationResult,
  type PasswordConfig,
  type UsernameConfig,
} from "@/utils/password";

/**
 * Composable for password and username validation
 * Provides reactive validation feedback
 */
export function usePasswordValidation(
  passwordConfig: PasswordConfig = PASSWORD_CONFIG,
  usernameConfig: UsernameConfig = USERNAME_CONFIG
) {
  // State
  const password = ref("");
  const username = ref("");

  // Validation results
  const passwordValidation = ref<ValidationResult>({ isValid: false, errors: [] });
  const usernameValidation = ref<ValidationResult>({ isValid: false, errors: [] });

  // Computed
  const isPasswordValid = computed(() => passwordValidation.value.isValid);
  const isUsernameValid = computed(() => usernameValidation.value.isValid);
  const passwordErrors = computed(() => passwordValidation.value.errors);
  const usernameErrors = computed(() => usernameValidation.value.errors);

  // Guidelines
  const passwordGuidelines = computed(() => getPasswordGuidelines(passwordConfig));
  const usernameGuidelines = computed(() => getUsernameGuidelines(usernameConfig));

  // Methods
  const validatePasswordField = (pwd: string) => {
    passwordValidation.value = validatePassword(pwd, passwordConfig);
    return isPasswordValid.value;
  };

  const validateUsernameField = (usr: string) => {
    usernameValidation.value = validateUsername(usr, usernameConfig);
    return isUsernameValid.value;
  };

  const checkPassword = (pwd: string) => {
    password.value = pwd;
    return validatePasswordField(pwd);
  };

  const checkUsername = (usr: string) => {
    username.value = usr;
    return validateUsernameField(usr);
  };

  const reset = () => {
    password.value = "";
    username.value = "";
    passwordValidation.value = { isValid: false, errors: [] };
    usernameValidation.value = { isValid: false, errors: [] };
  };

  return {
    // State
    password,
    username,
    
    // Validation results
    passwordValidation,
    usernameValidation,
    
    // Computed flags
    isPasswordValid,
    isUsernameValid,
    
    // Computed errors
    passwordErrors,
    usernameErrors,
    
    // Guidelines
    passwordGuidelines,
    usernameGuidelines,
    
    // Methods
    checkPassword,
    checkUsername,
    validatePasswordField,
    validateUsernameField,
    reset,
  };
}
